import { AlertTriangle, Camera, Check, ChevronLeft, ChevronRight, Circle, ImageOff, Mic, Play, RotateCcw, Square, SwitchCamera, Trash2, Upload, Volume2, X, Zap } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';


import { stripHtmlForLabel } from './formula-plaintext';
import { WaveformPlayer } from './sound-waveform-player';
import { useIsMobile, useIsMobilePortrait } from './task-content';
import { assetAccentColor } from './waveform-utils';

import type { Block } from '@/types/experience';

import Toast from '@/components/toast';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { SearchField } from '@/components/ui/search-field';
import { TextField } from '@/components/ui/text-field';
import { assetsApi, type AssetSummary, type AssetUsageDetail, type TrashedAsset } from '@/lib/assets-api';
import { IMAGE_ACCEPT, isAllowedImageMimeType } from '@/lib/image-uploads';

function formatDuration(seconds?: number): string {
  if (!seconds || !Number.isFinite(seconds)) return '0:00';
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// Small static thumbnail waveform for library grid tiles — no decode, no interactivity, just the
// server-precomputed peak summary rendered as bars. Falls back to a plain icon for older assets
// uploaded before this existed (no waveformPeaks stored yet).
function MiniWaveform({ peaks, className = '' }: { peaks?: number[]; className?: string }) {
  if (!peaks || peaks.length === 0) return <Volume2 className={`h-5 w-5 text-ws-muted ${className}`} />;
  return (
    <div className={`flex w-full items-end justify-center gap-px ${className}`}>
      {peaks.map((p, i) => (
        <span key={i} className='w-full min-w-px rounded-full bg-ws-muted' style={{ height: `${Math.max(15, p * 100)}%` }} />
      ))}
    </div>
  );
}

function formatUploadDate(createdAt: string, locale: string): string {
  return new Date(createdAt).toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
}

type Scope = 'recent' | 'experience' | 'all';

const SCOPE_KEYS: { value: Scope; key: string }[] = [
  { value: 'recent', key: 'scopeRecent' },
  { value: 'experience', key: 'scopeExperience' },
  { value: 'all', key: 'scopeAll' },
];

// Keep in sync with the backend's STORAGE_AUDIO_MAX_DURATION_SECONDS default (see sound-block.tsx).
const MAX_RECORD_SECONDS = 180;

// Keep in sync with storage.config.ts's own STORAGE_ALLOWED_IMAGE_MIME_TYPES/
// STORAGE_ALLOWED_AUDIO_MIME_TYPES defaults — the frontend has no runtime access to whatever the
// backend's env vars actually resolved to, so this checks against the SAME defaults as an early,
// friendlier rejection (the backend's own upload endpoint is still the authoritative check).
const ALLOWED_AUDIO_MIME_TYPES = ['audio/webm', 'audio/mp4', 'audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/m4a'];

const RECENT_LIMIT = 12;
// "Recent" (no search) stays a fixed small peek, not a paginated browse — everything else
// (This experience, All images, or any active search) pages through PAGE_SIZE at a time.
const PAGE_SIZE = 48;

interface TextOption {
  altText?: string;
  caption?: string;
  transcript?: string;
  motionDescription?: string;
  count: number;
  sampleExperienceTitle: string;
  sampleTaskTitle: string;
}

interface OptionWithMedia {
  id: string;
  media?: { kind: 'image' | 'audio'; assetId: string; altText?: string; transcript?: string; motionDescription?: string };
}

// Same staleness gap as computeEffectiveUsage below, applied to the actual alt/caption/transcript
// TEXT rather than just the count: the backend's usage rows only reflect the last SAVED state, so
// a transcript just typed into the currently-open block (not yet saved) would otherwise show as
// blank here. Patch in the live in-memory value for any usage row that belongs to the task
// currently being edited — everywhere else, there's no live view to fall back to.
function applyLiveOverrides(details: AssetUsageDetail[], currentTaskBlocks: Block[], currentTaskId: string): AssetUsageDetail[] {
  return details.map((d) => {
    if (d.taskId !== currentTaskId) return d;
    const liveBlock = currentTaskBlocks.find((b) => b.id === d.blockId);
    if (!liveBlock) return d;
    // A per-option usage (an MCQ/Ordering option's own media) reads that option's own
    // altText/transcript, not the block's top-level data — the two never share a namespace.
    if (d.optionId) {
      const data = liveBlock.data as { options?: OptionWithMedia[]; items?: OptionWithMedia[] };
      const liveOption = (data.options ?? data.items ?? []).find((o) => o.id === d.optionId);
      return liveOption ? { ...d, altText: liveOption.media?.altText, caption: undefined, transcript: liveOption.media?.transcript, motionDescription: liveOption.media?.motionDescription } : d;
    }
    const liveData = liveBlock.data as { altText?: string; caption?: string; transcript?: string; motionDescription?: string };
    return { ...d, altText: liveData.altText, caption: liveData.caption, transcript: liveData.transcript, motionDescription: liveData.motionDescription };
  });
}

// Collapses usage-detail rows into one entry per distinct alt/caption/transcript combo, so a
// picker doesn't show the same text five times just because five placements happen to share it verbatim.
//
// MOST USED FIRST, because the pager opens on the first option rather than on blank — so this
// order decides what a teacher gets by simply confirming. Frequency rather than recency: it is the
// text this asset is usually described with, which is the one most likely to be right again, and
// it is stable (a single odd placement cannot displace it the way "most recent" would). Recency is
// not available here anyway — `AssetUsage` is timestamped in Mongo but `AssetUsageDetail` carries
// no date to the client.
//
// `sort` is stable, so combos used the same number of times keep the order they were found in.
export function buildTextOptions(details: AssetUsageDetail[]): TextOption[] {
  const map = new Map<string, TextOption>();
  for (const d of details) {
    const key = `${d.altText ?? ''} ${d.caption ?? ''} ${d.transcript ?? ''} ${d.motionDescription ?? ''}`;
    const existing = map.get(key);
    if (existing) existing.count += 1;
    else map.set(key, { altText: d.altText, caption: d.caption, transcript: d.transcript, motionDescription: d.motionDescription, count: 1, sampleExperienceTitle: d.experienceTitle, sampleTaskTitle: d.taskTitle });
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
}

interface EffectiveUsage {
  blockCount: number;
  experienceCount: number;
}

// The backend's AssetUsage rows only reflect the last SAVED state — a block placed or removed in
// the current editing session (unsaved) is invisible to it until the next Save. So for the CURRENT
// task we trust the live in-memory blocks list instead, and only fall back to the backend's rows
// for placements elsewhere (other tasks/experiences, which we have no live view into).
function computeEffectiveUsage(assetId: string, backendDetails: AssetUsageDetail[], currentTaskBlocks: Block[], currentTaskId: string, currentExperienceId: string, assetKind: 'image' | 'audio'): EffectiveUsage {
  // Derived from the SELECTED asset's own kind, not a modal-wide fixed kind — matters once the
  // modal can browse 'any' kind, where there's no single kind to pin this to.
  const blockType: 'image' | 'sound' = assetKind === 'audio' ? 'sound' : 'image';
  const otherTaskDetails = backendDetails.filter((d) => d.taskId !== currentTaskId);
  // Distinct blockId, not a row count — one block can own several option-level usage rows for
  // the SAME asset (e.g. two MCQ options on one question sharing an image), which would otherwise
  // overstate the count (mirrors the same fix in the backend's own computeUsage).
  const otherTaskBlockIds = new Set(otherTaskDetails.map((d) => d.blockId));
  const localBlockIds = new Set(
    currentTaskBlocks
      .filter((b) => {
        if (b.type === blockType) return (b.data as { assetId?: string }).assetId === assetId;
        if (b.type === 'mcq' || b.type === 'ordering') {
          const data = b.data as { options?: OptionWithMedia[]; items?: OptionWithMedia[] };
          return (data.options ?? data.items ?? []).some((o) => o.media?.assetId === assetId && o.media?.kind === assetKind);
        }
        return false;
      })
      .map((b) => b.id),
  );
  const experienceIds = new Set(otherTaskDetails.map((d) => d.experienceId));
  if (localBlockIds.size > 0) experienceIds.add(currentExperienceId);
  return { blockCount: otherTaskBlockIds.size + localBlockIds.size, experienceCount: experienceIds.size };
}

interface LibraryModalProps {
  /** 'any' browses/uploads either kind in one modal — used by MCQ/Ordering's per-option media
   * picker, where the teacher hasn't committed to a kind ahead of time (unlike the Image/Sound
   * blocks, which always pass a fixed 'image'/'audio'). */
  assetKind: 'image' | 'audio' | 'any';
  experienceId: string;
  currentAssetId: string | null;
  currentTaskId: string;
  currentTaskBlocks: Block[];
  onSelect: (asset: AssetSummary, chosen: { altText?: string; caption?: string; transcript?: string; motionDescription?: string }) => void;
  onClose: () => void;
}

/**
 * Above this many flashes a second, the library says so.
 *
 * WCAG 2.3.1's own line is three. This warns ABOVE three rather than at it, so an animation
 * measured exactly at the limit is not accused of crossing it — the measurement is a mean over a
 * downscaled copy and the standard's own threshold depends on a screen size we cannot know, so the
 * margin belongs on the side that does not cry wolf.
 */
const FLASH_WARN_PER_SECOND = 3;

export function LibraryModal({ assetKind, experienceId, currentAssetId, currentTaskId, currentTaskBlocks, onSelect, onClose }: LibraryModalProps) {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const isMobilePortrait = useIsMobilePortrait();
  const renameInputId = useId();
  const overlayRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const swapInputRef = useRef<HTMLInputElement>(null);
  const swapTargetIdRef = useRef<string | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const swapVideoRef = useRef<HTMLVideoElement>(null);
  const swapStreamRef = useRef<MediaStream | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const scopeRadioRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const viewTrashButtonRef = useRef<HTMLButtonElement>(null);
  const trashBackButtonRef = useRef<HTMLButtonElement>(null);
  const detailCloseButtonRef = useRef<HTMLButtonElement>(null);
  const swapRegionRef = useRef<HTMLDivElement>(null);
  // Separate from swapRegionRef — a fresh (not-replacing) capture renders in the grid pane, which
  // can be mounted AT THE SAME TIME as the detail panel's own swapRegionRef (desktop: a selection
  // exists while a fresh capture is triggered from the header), so this needs its own landing spot.
  const freshCaptureRegionRef = useRef<HTMLDivElement>(null);

  const titleId = useId();
  const [scope, setScope] = useState<Scope>('recent');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [results, setResults] = useState<AssetSummary[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [usageDetails, setUsageDetails] = useState<AssetUsageDetail[] | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [pagerIndex, setPagerIndex] = useState(0);
  const [renameValue, setRenameValue] = useState('');
  const [deleteArmed, setDeleteArmed] = useState(false);

  // Replace is always gated: Upload/Camera only appear after an explicit confirm, regardless of
  // usage count — this is a deliberate extra step against accidental replacement, not just a
  // usage-based warning.
  const [swapConfirmPromptOpen, setSwapConfirmPromptOpen] = useState(false);
  const [swapConfirmed, setSwapConfirmed] = useState(false);

  const [swapCameraActive, setSwapCameraActive] = useState(false);
  const [swapCameraFacing, setSwapCameraFacing] = useState<'environment' | 'user'>('environment');
  const [swapCameraError, setSwapCameraError] = useState('');

  // Audio's swap-via-re-record parallel to the image camera flow above — same gated-replace
  // shape, MediaRecorder instead of getUserMedia+canvas.
  const [swapRecordingActive, setSwapRecordingActive] = useState(false);
  const [swapRecordingError, setSwapRecordingError] = useState('');
  const [swapRecordingElapsed, setSwapRecordingElapsed] = useState(0);
  const swapMediaRecorderRef = useRef<MediaRecorder | null>(null);
  const swapRecordedChunksRef = useRef<Blob[]>([]);
  const swapRecordingStreamRef = useRef<MediaStream | null>(null);
  const swapRecordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [showTrash, setShowTrash] = useState(false);
  const [trashed, setTrashed] = useState<TrashedAsset[] | null>(null);
  const [trashLoading, setTrashLoading] = useState(false);
  const [purgeArmedId, setPurgeArmedId] = useState<string | null>(null);

  const selectedAsset = results?.find((a) => a.id === selectedAssetId) ?? null;
  const effectiveUsage = selectedAsset && usageDetails
    ? computeEffectiveUsage(selectedAsset.id, usageDetails, currentTaskBlocks, currentTaskId, experienceId, selectedAsset.kind)
    : null;

  // Resolves off the SELECTED asset's own kind — for anything scoped to one specific asset
  // (detail panel: title, swap controls, delete/rename copy, alt-text/transcript labels), which is
  // always 100% kind-accurate this way regardless of assetKind mode. Falls back to the modal's own
  // `assetKind` (or 'image') only when nothing is selected yet.
  const libraryNs = (selectedAsset?.kind ?? (assetKind === 'audio' ? 'audio' : 'image')) === 'audio'
    ? 'blocks.sound.library' : 'blocks.image.library';
  // Small wrapper so every library.* lookup automatically resolves against the right namespace
  // for the asset kind currently in view (image vs. audio strings never mix).
  const tl = (key: string, opts?: Record<string, unknown>): string => t(`${libraryNs}.${key}`, opts as never) as unknown as string;
  // For the handful of GRID-level chrome strings (scope label, search placeholder, empty states,
  // list-load error) that are visible regardless of any specific selection — these must read
  // neutral in 'any' mode (desktop shows grid + detail panel side by side, so gating this on
  // "nothing selected yet" would be wrong; the grid's own chrome never describes one asset).
  const gridT = (key: string, opts?: Record<string, unknown>): string =>
    assetKind === 'any'
      ? (t(`common.mediaLibrary${key.charAt(0).toUpperCase()}${key.slice(1)}`, opts as never) as unknown as string)
      : tl(key, opts);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [search]);

  const paginated = scope !== 'recent' || !!debouncedSearch;

  useEffect(() => {
    if (showTrash) return;
    let cancelled = false;
    setLoading(true);
    assetsApi.list(assetKind, {
      search: debouncedSearch || undefined,
      experienceId: scope === 'experience' ? experienceId : undefined,
      limit: paginated ? PAGE_SIZE : RECENT_LIMIT,
    })
      .then((r) => { if (!cancelled) { setResults(r.items); setHasMore(r.hasMore); } })
      .catch(() => { if (!cancelled) setToastMsg(gridT('loadError')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, debouncedSearch, experienceId, showTrash, assetKind, t]);

  // Auto-open the currently-placed asset's own detail panel (alt-text/transcript pager included)
  // on mount, instead of leaving the teacher to notice its small "currently used" badge in the
  // grid and click it themselves — that badge is easy to miss, and was previously the ONLY way to
  // reach the alt-text editing UI for an already-selected image/sound.
  useEffect(() => {
    if (!currentAssetId) return;
    assetsApi.get(currentAssetId)
      .then((asset) => {
        setResults((prev) => (prev?.some((a) => a.id === asset.id) ? prev : [asset, ...(prev ?? [])]));
        selectAsset(asset);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function loadMore() {
    if (!results || loadingMore) return;
    setLoadingMore(true);
    assetsApi.list(assetKind, {
      search: debouncedSearch || undefined,
      experienceId: scope === 'experience' ? experienceId : undefined,
      limit: PAGE_SIZE,
      skip: results.length,
    })
      .then((r) => { setResults((prev) => [...(prev ?? []), ...r.items]); setHasMore(r.hasMore); })
      .catch(() => setToastMsg(gridT('loadError')))
      .finally(() => setLoadingMore(false));
  }

  function loadTrash() {
    setTrashLoading(true);
    assetsApi.trash(assetKind)
      .then(setTrashed)
      .catch(() => setToastMsg(gridT('loadError')))
      .finally(() => setTrashLoading(false));
  }

  function openTrash() {
    setShowTrash(true);
    setSelectedAssetId(null);
    loadTrash();
  }

  // Lock background scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Return focus to whatever triggered the modal once it closes, rather than dropping it to body.
  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    return () => previouslyFocusedRef.current?.focus?.();
  }, []);

  // Trash is a separate sub-view whose own toggle button unmounts on entry/exit — without this,
  // focus would fall back to the document body each time.
  useEffect(() => {
    if (showTrash) trashBackButtonRef.current?.focus();
    else viewTrashButtonRef.current?.focus();
  }, [showTrash]);

  // Mobile's "selected asset" view fully replaces the grid (its own trigger tile unmounts with it),
  // so — same reasoning as Trash above — move focus into the new region rather than losing it.
  useEffect(() => {
    if (!isMobile || showTrash) return;
    if (selectedAssetId) detailCloseButtonRef.current?.focus();
    else searchInputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssetId, isMobile]);

  // The replace flow's own stages (gate button → confirm → upload/camera choices) each remove the
  // control that was just clicked to advance them. If that leaves focus on nothing (body), land on
  // the region itself instead of silently dropping the keyboard/screen-reader user.
  useEffect(() => {
    if (document.activeElement !== document.body) return;
    (isReplacingSelected ? swapRegionRef : freshCaptureRegionRef).current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swapConfirmPromptOpen, swapConfirmed, swapCameraActive, swapRecordingActive]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Focus trap — keep Tab inside the overlay
  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;
    function onTab(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const focusable = Array.from(
        el!.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
      ).filter((n) => !!(n.offsetWidth || n.offsetHeight || n.getClientRects().length));
      if (!focusable.length) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    el.addEventListener('keydown', onTab);
    return () => el.removeEventListener('keydown', onTab);
  }, []);

  useEffect(() => { searchInputRef.current?.focus(); }, []);

  function stopSwapCamera() {
    swapStreamRef.current?.getTracks().forEach((track) => track.stop());
    swapStreamRef.current = null;
  }

  async function startSwapCamera() {
    setSwapCameraError('');
    stopSwapCamera();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: swapCameraFacing } });
      swapStreamRef.current = stream;
      if (swapVideoRef.current) swapVideoRef.current.srcObject = stream;
    } catch (err) {
      setSwapCameraError(err instanceof Error ? err.message : t('blocks.image.cameraError'));
    }
  }

  function flipSwapCamera() {
    setSwapCameraFacing((f) => (f === 'environment' ? 'user' : 'environment'));
  }

  useEffect(() => {
    if (swapCameraActive) void startSwapCamera(); else stopSwapCamera();
    return () => stopSwapCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swapCameraActive, swapCameraFacing]);

  useEffect(() => () => stopSwapRecordingStream(), []);

  function stopSwapRecordingStream() {
    swapRecordingStreamRef.current?.getTracks().forEach((track) => track.stop());
    swapRecordingStreamRef.current = null;
    if (swapRecordingTimerRef.current) { clearInterval(swapRecordingTimerRef.current); swapRecordingTimerRef.current = null; }
  }

  async function startSwapRecording() {
    setSwapRecordingError('');
    setSwapRecordingElapsed(0);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      swapRecordingStreamRef.current = stream;
      swapRecordedChunksRef.current = [];
      const recorder = new MediaRecorder(stream);
      swapMediaRecorderRef.current = recorder;
      recorder.ondataavailable = (e) => { if (e.data.size > 0) swapRecordedChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(swapRecordedChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        const file = new File([blob], `recording-${Date.now()}.webm`, { type: blob.type });
        stopSwapRecordingStream();
        setSwapRecordingActive(false);
        const targetId = swapTargetIdRef.current;
        // null targetId = a fresh recording (triggered from the header's "Record Sound" button,
        // not replacing an existing asset) — the same distinction handleSwapFileChange already
        // makes for the file-picker path.
        if (targetId) void performSwap(targetId, file); else void performFreshUpload(file);
      };
      recorder.start();
      swapRecordingTimerRef.current = setInterval(() => {
        setSwapRecordingElapsed((prev) => {
          const next = prev + 1;
          if (next >= MAX_RECORD_SECONDS) stopSwapRecording();
          return next;
        });
      }, 1000);
    } catch (err) {
      setSwapRecordingError(err instanceof Error ? err.message : t('blocks.sound.micError'));
    }
  }

  function stopSwapRecording() {
    swapMediaRecorderRef.current?.stop();
  }

  function cancelSwapRecording() {
    stopSwapRecordingStream();
    setSwapRecordingActive(false);
    swapMediaRecorderRef.current = null;
  }

  function selectAsset(asset: AssetSummary) {
    setSelectedAssetId(asset.id);
    setDeleteArmed(false);
    setSwapConfirmPromptOpen(false);
    setSwapConfirmed(false);
    setSwapCameraActive(false);
    setSwapRecordingActive(false);
    setRenameValue(asset.filename);
    setPagerIndex(0);
    setUsageDetails(null);
    setUsageLoading(true);
    assetsApi.usageDetails(asset.id)
      .then(setUsageDetails)
      .catch(() => setToastMsg(tl('loadError')))
      .finally(() => setUsageLoading(false));
  }

  // Re-fetches an asset to get a freshly-signed file URL — passed to the detail-panel waveform
  // player as onRetry, since a playback load failure there most likely means the 1-hour signed URL
  // just expired, which retrying the SAME url can never recover from.
  function refreshAsset(assetId: string) {
    assetsApi.get(assetId)
      .then((updated) => setResults((prev) => prev?.map((a) => (a.id === assetId ? updated : a)) ?? prev))
      .catch(() => setToastMsg(tl('loadError')));
  }

  async function saveRename(asset: AssetSummary) {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === asset.filename) { setRenameValue(asset.filename); return; }
    try {
      const updated = await assetsApi.update(asset.id, { filename: trimmed });
      setResults((prev) => prev?.map((a) => (a.id === asset.id ? updated : a)) ?? prev);
    } catch {
      setToastMsg(tl('renameError'));
      setRenameValue(asset.filename);
    }
  }

  async function handleDelete(asset: AssetSummary) {
    setBusy(true);
    try {
      await assetsApi.remove(asset.id);
      setResults((prev) => prev?.filter((a) => a.id !== asset.id) ?? prev);
      setSelectedAssetId(null);
    } catch (err) {
      // The UI already keeps this button inert whenever the asset is known to be in use (see
      // deleteBlocked below) — this is defense-in-depth for a race the client can't see coming
      // (e.g. another session placed the asset between our last usage check and this call), so
      // it's worth surfacing the real reason rather than a generic failure.
      const inUse = err instanceof Error && err.message === 'Asset is in use';
      setToastMsg(inUse && effectiveUsage
        ? tl('deleteBlockedInUse', { blockCount: effectiveUsage.blockCount })
        : tl('deleteError'));
    } finally {
      setBusy(false);
      setDeleteArmed(false);
    }
  }

  // Trash items aren't `selectedAsset` (separate array/state entirely), so `tl()` can't resolve
  // per-item here the way it does for the grid/detail panel — in 'any' mode it would otherwise
  // fall through to the modal-default namespace regardless of THIS item's real kind (e.g. an audio
  // clip's restore/purge failure reading "Could not restore this image"). Resolves directly off
  // the specific trashed asset's own kind instead.
  function tlFor(asset: { kind: 'image' | 'audio' }, key: string): string {
    const ns = asset.kind === 'audio' ? 'blocks.sound.library' : 'blocks.image.library';
    return t(`${ns}.${key}`) as unknown as string;
  }

  async function handleRestore(asset: TrashedAsset) {
    setBusy(true);
    try {
      await assetsApi.restore(asset.id);
      setTrashed((prev) => prev?.filter((a) => a.id !== asset.id) ?? prev);
    } catch {
      setToastMsg(tlFor(asset, 'restoreError'));
    } finally {
      setBusy(false);
    }
  }

  async function handlePurgeForever(asset: TrashedAsset) {
    setBusy(true);
    try {
      await assetsApi.purgeForever(asset.id);
      setTrashed((prev) => prev?.filter((a) => a.id !== asset.id) ?? prev);
    } catch {
      setToastMsg(tlFor(asset, 'purgeError'));
    } finally {
      setBusy(false);
      setPurgeArmedId(null);
    }
  }

  // Shared by both the file-picker path and the live-capture path — same target asset, same result handling.
  async function performSwap(targetId: string, file: File) {
    setBusy(true);
    try {
      const { asset } = await assetsApi.swap(targetId, file);
      setResults((prev) => prev?.map((a) => (a.id === targetId ? asset : a)) ?? prev);
    } catch (err) {
      setToastMsg(err instanceof Error ? err.message : t(selectedAsset?.kind === 'audio' ? 'blocks.sound.uploadError' : 'blocks.image.uploadError'));
    } finally {
      setBusy(false);
      // Require a fresh confirm before the NEXT replace too.
      setSwapConfirmed(false);
    }
  }

  function confirmSwapGate() {
    setSwapConfirmPromptOpen(false);
    setSwapConfirmed(true);
  }

  // Uploads a genuinely NEW asset (not replacing anything) and selects it — the counterpart to
  // performSwap for the "add" path, which this modal never had before: it could only browse/
  // replace an already-uploaded asset, never create one from scratch.
  async function performFreshUpload(file: File) {
    setBusy(true);
    try {
      const isAudio = file.type.startsWith('audio/');
      const uploaded = isAudio ? await assetsApi.uploadAudio(file) : await assetsApi.upload(file);
      setResults((prev) => [uploaded, ...(prev ?? [])]);
      selectAsset(uploaded);
    } catch (err) {
      setToastMsg(err instanceof Error ? err.message : t(file.type.startsWith('audio/') ? 'blocks.sound.uploadError' : 'blocks.image.uploadError'));
    } finally {
      setBusy(false);
    }
  }

  async function handleSwapFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const targetId = swapTargetIdRef.current;
    // null targetId = the "Add" trigger (no existing asset to replace) — accept either kind here,
    // since the modal may be browsing in 'any' mode; the file's own MIME type decides which.
    if (!targetId) {
      if (!isAllowedImageMimeType(file.type) && !ALLOWED_AUDIO_MIME_TYPES.includes(file.type)) {
        setToastMsg(t('common.unsupportedFileType'));
        return;
      }
      void performFreshUpload(file);
      return;
    }
    const expectedPrefix = selectedAsset?.kind === 'audio' ? 'audio/' : 'image/';
    if (!file.type.startsWith(expectedPrefix)) return;
    void performSwap(targetId, file);
  }

  function captureSwapPhoto() {
    const video = swapVideoRef.current;
    // null targetId = a fresh photo (triggered from the header's "Take Photo" button, not
    // replacing an existing asset) — same distinction as the recorder's onstop handler above.
    const targetId = swapTargetIdRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
      if (targetId) void performSwap(targetId, file); else void performFreshUpload(file);
    }, 'image/jpeg', 0.9);
    setSwapCameraActive(false);
  }

  function daysUntilPurge(purgeAt: string): number {
    return Math.max(0, Math.ceil((new Date(purgeAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
  }

  function handleUseImage() {
    if (!selectedAsset) return;
    if (pagerIndex === 0) { onSelect(selectedAsset, {}); return; }
    const opt = textOptions[pagerIndex - 1];
    onSelect(selectedAsset, { altText: opt?.altText, caption: opt?.caption, transcript: opt?.transcript, motionDescription: opt?.motionDescription });
  }

  const textOptions = usageDetails ? buildTextOptions(applyLiveOverrides(usageDetails, currentTaskBlocks, currentTaskId)) : [];
  const totalPagerSteps = textOptions.length + 1;
  const currentOption = pagerIndex === 0 ? null : textOptions[pagerIndex - 1];

  /**
   * OPEN ON THE MOST-USED TEXT, not on blank.
   *
   * The pager always reset to blank, so the alt text a teacher had already written for this image
   * was one chevron away and never the thing they landed on — a control there is no reason to go
   * looking for. The reported result was placing the image, noticing it had no alt text, deleting
   * the block and fetching it again.
   *
   * Blank does not go away; it stops being the default. It is step 0, so it is one click LEFT of
   * where you open, and the second-most-used text is one click right.
   *
   * In an EFFECT keyed on the resolved usage, not in `selectAsset`, because the options arrive
   * from a fetch that lands well after the click. Keyed on `usageDetails` rather than on
   * `textOptions` deliberately: `textOptions` is rebuilt on every render (it folds in live edits
   * to the open task), so depending on it would drag the pager back to the default while a
   * teacher was still paging through it.
   */
  useEffect(() => {
    if (!usageDetails) return;
    setPagerIndex(textOptions.length > 0 ? 1 : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssetId, usageDetails]);
  const showEmptyExperienceFallback = scope === 'experience' && !search.trim() && results !== null && results.length === 0;
  // Also blocked while usage is still loading (usageDetails hasn't resolved yet after selecting
  // an asset) — not just once it resolves in-use — otherwise the delete button is briefly a live,
  // un-disabled button for an asset that may turn out to be in use, letting a fast arm+confirm
  // slip through before the check finishes and hit the backend's 409 instead of being inert.
  const deleteBlocked = usageLoading || (!!effectiveUsage && effectiveUsage.blockCount > 0);

  // The "add a brand new asset" entry point — this modal previously only let a teacher browse or
  // replace an already-uploaded asset, never upload one from scratch. Always visible above the
  // grid (not gated on a selection), unlike the swap flow's own upload button.
  const addNewButton = (
    /* Primary: adding media is what this modal exists to do, and it had been wearing secondary
       clothes at the app's smallest text size, which is why the row read as flat. */
    <Button variant='primary' size='sm' icon={<Upload />} disabled={busy} className='shrink-0'
      onClick={() => { swapTargetIdRef.current = null; swapInputRef.current?.click(); }}>
      {assetKind === 'any' ? t('common.uploadMedia') : tl('addNew')}
    </Button>
  );

  // Fresh (not-replacing) capture entry points, next to Upload Media — the same live capture the
  // swap/replace flow already had, now also available for adding a brand-new asset. Both show in
  // 'any' mode (a teacher hasn't committed to a kind yet); only the matching one shows for the
  // Image/Sound blocks' own fixed-kind picker.
  const showTakePhoto = assetKind === 'any' || assetKind === 'image';
  const showRecordSound = assetKind === 'any' || assetKind === 'audio';
  const takePhotoButton = showTakePhoto && (
    <Button size='sm' icon={<Camera />} disabled={busy} className='shrink-0'
      onClick={() => { swapTargetIdRef.current = null; setSwapCameraActive(true); }}>
      {t('common.takePhoto')}
    </Button>
  );
  const recordSoundButton = showRecordSound && (
    <Button size='sm' icon={<Mic />} disabled={busy} className='shrink-0'
      onClick={() => { swapTargetIdRef.current = null; setSwapRecordingActive(true); void startSwapRecording(); }}>
      {t('common.recordSound')}
    </Button>
  );

  // Whether the active camera/recording capture is REPLACING the currently selected asset, versus
  // a fresh add triggered from the header buttons above (swapTargetIdRef is null in that case) —
  // both share the same swapCameraActive/swapRecordingActive state, so this is what keeps the
  // capture UI from appearing in both the detail panel AND the main grid pane at once.
  const isReplacingSelected = swapTargetIdRef.current !== null && swapTargetIdRef.current === selectedAssetId;

  // Shared by the detail panel's replace flow and the grid pane's fresh-add flow — same capture
  // UI either way, just placed in a different spot in the tree depending on isReplacingSelected.
  const cameraCaptureUI = (
    <div className='flex flex-col gap-2'>
      {swapCameraError ? (
        <div className='flex flex-col items-center gap-2 rounded-lg border-2 border-dashed border-ws-border py-6 px-4 text-center'>
          <Camera className='h-6 w-6 text-ws-muted' />
          <p role='alert' className='text-xs text-ws-text'>{t('blocks.image.cameraUnavailable')}</p>
          <button type='button' onClick={() => void startSwapCamera()} className='mt-1 rounded-md border border-ws-border px-2.5 py-1 text-xs font-medium text-ws-text hover:bg-ws-hover'>
            {t('blocks.image.tryAgain')}
          </button>
        </div>
      ) : (
        <div className='relative'>
          <video ref={swapVideoRef} autoPlay playsInline muted className='w-full rounded-lg bg-ws-btn aspect-video object-cover' />
          <button
            type='button'
            onClick={flipSwapCamera}
            aria-label={t('blocks.image.flipCamera')}
            title={t('blocks.image.flipCamera')}
            className='absolute top-2 right-2 flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80'
          >
            <SwitchCamera className='h-4 w-4' />
          </button>
        </div>
      )}
      <div className='flex items-center justify-center gap-3'>
        <button type='button' onClick={() => setSwapCameraActive(false)} className='rounded-md border border-ws-border px-3 py-1.5 text-xs font-medium text-ws-text hover:bg-ws-hover'>
          {t('blocks.image.cancelCamera')}
        </button>
        {!swapCameraError && (
          <button
            type='button'
            onClick={captureSwapPhoto}
            disabled={busy}
            aria-label={t('blocks.image.capture')}
            title={t('blocks.image.capture')}
            className='flex h-12 w-12 items-center justify-center rounded-full bg-ws-primary text-white shadow-lg hover:opacity-90 disabled:opacity-50'
          >
            <Camera className='h-5 w-5' />
          </button>
        )}
      </div>
    </div>
  );

  const recordingCaptureUI = (
    <div className='flex flex-col items-center gap-3 rounded-lg border-2 border-dashed border-ws-border py-6 px-4 text-center'>
      {swapRecordingError ? (
        <>
          <Mic className='h-6 w-6 text-ws-muted' />
          <p role='alert' className='text-xs text-ws-text'>{t('blocks.sound.micUnavailable')}</p>
          <button type='button' onClick={() => void startSwapRecording()} className='mt-1 rounded-md border border-ws-border px-2.5 py-1 text-xs font-medium text-ws-text hover:bg-ws-hover'>
            {t('blocks.sound.tryAgain')}
          </button>
        </>
      ) : (
        <>
          <div className='flex items-center gap-2 text-ws-warning-fg'>
            <Circle className='h-3 w-3 animate-pulse fill-current' />
            <span className='text-sm font-medium'>{t('blocks.sound.recordingInProgress')}</span>
          </div>
          <p className='text-xl font-semibold tabular-nums text-ws-text'>{formatDuration(swapRecordingElapsed)}</p>
        </>
      )}
      <div className='flex items-center justify-center gap-3'>
        <button type='button' onClick={cancelSwapRecording} className='rounded-md border border-ws-border px-3 py-1.5 text-xs font-medium text-ws-text hover:bg-ws-hover'>
          {t('common.cancel')}
        </button>
        {!swapRecordingError && (
          <button
            type='button'
            onClick={stopSwapRecording}
            disabled={busy}
            aria-label={t('blocks.sound.stopRecording')}
            title={t('blocks.sound.stopRecording')}
            className='flex h-12 w-12 items-center justify-center rounded-full bg-ws-primary text-white shadow-lg hover:opacity-90 disabled:opacity-50'
          >
            <Square className='h-5 w-5' />
          </button>
        )}
      </div>
    </div>
  );

  const gridView = (swapCameraActive || swapRecordingActive) && !isReplacingSelected ? (
    // tabIndex=-1 fallback focus target — see the focus-rescue effect above: this stage removes
    // the header button that was just clicked to reach it (Take Photo/Record Sound), same
    // "focus needs somewhere to land" reasoning as the detail panel's swapRegionRef.
    <div ref={freshCaptureRegionRef} tabIndex={-1} className='mx-auto max-w-sm py-4 outline-none'>
      {swapCameraActive ? cameraCaptureUI : recordingCaptureUI}
    </div>
  ) : loading ? (
    <p className='py-8 text-center text-sm text-ws-muted'>{tl('loading')}</p>
  ) : showEmptyExperienceFallback ? (
    <div className='flex flex-col items-center gap-3 py-8 text-center'>
      <p className='text-sm text-ws-muted'>{gridT('emptyExperience')}</p>
      <Button size='sm' onClick={() => setScope('all')}>
        {gridT('searchAllInstead')}
      </Button>
    </div>
  ) : !results || results.length === 0 ? (
    <p className='py-8 text-center text-sm text-ws-muted'>{gridT('empty')}</p>
  ) : (
    <div className='flex flex-col gap-3'>
      <div className='grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6'>
        {results.map((asset) => {
          const isSelected = selectedAssetId === asset.id;
          const isCurrent = asset.id === currentAssetId;
          return (
            <button
              key={asset.id}
              type='button'
              onClick={() => selectAsset(asset)}
              className={`relative flex flex-col gap-1 rounded-lg border p-1 text-left hover:bg-ws-hover ${
                isSelected ? 'border-ws-primary' : 'border-ws-border'
              }`}
            >
              {isCurrent && (
                <span className='absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-ws-primary text-white' aria-label={tl('currentlyUsed')}>
                  <Check className='h-2.5 w-2.5' />
                </span>
              )}
              {asset.kind === 'audio' ? (
                <div
                  className='flex aspect-square w-full flex-col items-center justify-center gap-1 rounded p-1'
                  style={{ backgroundColor: assetAccentColor(asset.id) }}
                >
                  <MiniWaveform peaks={asset.waveformPeaks} className='flex-1' />
                  <span className='text-[9px] text-ws-muted'>{formatDuration(asset.durationSeconds)}</span>
                </div>
              ) : asset.broken ? (
                /* Says so, rather than showing a blank square and leaving the teacher to guess
                   whether it is broken or still loading. */
                <div className='flex aspect-square w-full flex-col items-center justify-center gap-1 rounded border border-dashed border-ws-border bg-ws-hover p-1'>
                  <ImageOff className='h-4 w-4 text-ws-muted' aria-hidden />
                  <span className='text-center text-[9px] leading-tight text-ws-muted'>{tl('fileMissing')}</span>
                </div>
              ) : (
                <>
                  <img src={asset.urls.thumb} alt='' loading='lazy' className='aspect-square w-full rounded object-cover' />
                  {/* THE THUMBNAIL IS A STILL, deliberately — a grid of looping animations is both
                      heavy to render and impossible to scan — so without a mark there is nothing to
                      tell an animation from a static image until you select it. Bottom-left, since
                      the "currently used" tick already owns the top-right, and it carries a label
                      rather than relying on the glyph alone. */}
                  {asset.animated && (
                    /* A filled disc in the picture's own bottom-right corner, built to the same
                       recipe as the currently-used tick above it (same size, same shape, white
                       glyph) so the two read as one family — dark rather than primary, because
                       this describes what the asset IS and the tick describes a selection.
                       `bottom-7` clears the filename line below the thumbnail.

                       Glyph only: `formatDuration` is minutes:seconds and a loop is usually well
                       under a second, so a badge reading "0:00" would be worse than none. */
                    <span
                      className='absolute bottom-7 right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-black/70 text-white ring-1 ring-white/40'
                      aria-label={tl('animatedBadge')}
                      title={tl('animatedBadge')}
                    >
                      <Play className='h-2 w-2' aria-hidden fill='currentColor' />
                    </span>
                  )}
                  {/* FLASHING, beside the animated badge rather than instead of it: the two say
                      different things, and an animation that flashes is still an animation.
                      Warning-coloured, because this one is a reason to look before inserting.
                      The count is in the LABEL, not on the badge — a numeral at 8px is not read,
                      and the sentence a teacher needs ("5 times a second") does not fit anyway. */}
                  {asset.flashesPerSecond !== undefined && asset.flashesPerSecond > FLASH_WARN_PER_SECOND && (
                    <span
                      className='absolute bottom-7 right-7 flex h-4 w-4 items-center justify-center rounded-full bg-ws-warning-fg text-white ring-1 ring-white/40'
                      aria-label={tl('flashBadge', { count: asset.flashesPerSecond })}
                      title={tl('flashBadge', { count: asset.flashesPerSecond })}
                    >
                      <Zap className='h-2.5 w-2.5' aria-hidden fill='currentColor' />
                    </span>
                  )}
                </>
              )}
              <span className='truncate text-[10px] text-ws-muted'>{asset.filename}</span>
              {asset.kind === 'audio' && asset.description && (
                <span className='truncate text-[9px] text-ws-muted'>{asset.description}</span>
              )}
            </button>
          );
        })}
      </div>
      {paginated && hasMore && (
        <Button
          size='sm'
          busy={loadingMore}
          onClick={loadMore}
          className='self-center'
        >
          {loadingMore ? tl('loading') : tl('loadMore')}
        </Button>
      )}
    </div>
  );

  const detailBody = selectedAsset && (
    <>
      <div className='flex items-center justify-between gap-2'>
        <h3 className='text-sm font-semibold text-ws-text'>{tl('detailsTitle')}</h3>
        <button ref={detailCloseButtonRef} type='button' aria-label={t('common.close')} onClick={() => setSelectedAssetId(null)} className='rounded-md p-1 text-ws-muted hover:bg-ws-hover hover:text-ws-text'>
          <X className='h-4 w-4' />
        </button>
      </div>

      {/* tabIndex=-1 fallback focus target — see the swapRegionRef effect above: each stage of
          this region (camera view, replace-confirm, upload/camera choices) removes the control
          that was just clicked to reach it, so focus needs somewhere to land. */}
      <div ref={swapRegionRef} tabIndex={-1} className='flex flex-col gap-3 outline-none'>
      {swapCameraActive && isReplacingSelected ? (
        cameraCaptureUI
      ) : swapRecordingActive && isReplacingSelected ? (
        recordingCaptureUI
      ) : selectedAsset.kind === 'audio' ? (
        <WaveformPlayer src={selectedAsset.urls.audio} t={t} onRetry={() => refreshAsset(selectedAsset.id)} />
      ) : (
        <img src={selectedAsset.urls.display} alt={selectedAsset.filename} className='max-h-48 w-full rounded-lg bg-ws-btn object-contain' />
      )}

      {/* Rename, with Replace beside it.

          Replace used to be a full-width button on a row of its own, which cost ~50px of a panel
          whose whole job is to show you enough text to decide with. It is a rare, deliberate
          action -- it changes every existing placement at once -- so it does not need to be the
          widest control here; it needs to be reachable and clearly labelled, which an icon button
          with a name is. The confirm it opens still appears below, at full width, because THAT is
          the moment that deserves the room. */}
      <div className='flex flex-col'>
        <label htmlFor={renameInputId} className='mb-1 text-xs font-medium text-ws-text'>{tl('nameLabel')}</label>
        <div className='flex items-center gap-1.5'>
          <TextField
            variant='prose'
            id={renameInputId}
            value={renameValue}
            onChange={setRenameValue}
            onBlur={() => saveRename(selectedAsset)}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            className='min-w-0 flex-1'
            ariaLabel={tl('nameLabel')} />
          {!swapConfirmed && !swapConfirmPromptOpen && (
            <IconButton
              size='md'
              aria-label={tl('swapGate')}
              title={tl('swapGate')}
              onClick={() => { swapTargetIdRef.current = selectedAsset.id; setSwapConfirmPromptOpen(true); }}
            >
              <SwitchCamera className='h-4 w-4' />
            </IconButton>
          )}
        </div>
      </div>

      <p className='text-xs text-ws-muted'>{tl('uploadedOn', { date: formatUploadDate(selectedAsset.createdAt, i18n.language) })}</p>

      {/* Replace — always gated behind an explicit confirm before Upload/Camera appear, since
          replacing an asset's content affects every existing placement at once. */}
      {!swapConfirmed ? (
        swapConfirmPromptOpen ? (
          <div className='flex flex-col gap-2 rounded-lg border border-ws-warning-fg bg-ws-warning-bg p-3'>
            <div className='flex items-center justify-center gap-1.5 text-center'>
              <AlertTriangle aria-hidden='true' className='h-3.5 w-3.5 shrink-0 text-ws-warning-fg' />
              <p className='text-xs text-ws-warning-fg'>{tl('replaceConfirmMessage')}</p>
            </div>
            <div className='flex justify-center gap-2'>
              <Button size='sm' onClick={() => setSwapConfirmPromptOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button variant='primary' size='sm' onClick={confirmSwapGate}>
                {tl('replaceConfirmContinue')}
              </Button>
            </div>
          </div>
        ) : null
      ) : (
        <div className='flex items-center gap-2'>
          <Button size='sm' icon={<Upload />} disabled={busy} className='flex-1'
            onClick={() => { swapTargetIdRef.current = selectedAsset.id; swapInputRef.current?.click(); }}>
            {tl('swapUpload')}
          </Button>
          {selectedAsset.kind === 'image' ? (
            <Button size='sm' icon={<Camera />} disabled={busy} className='flex-1'
              onClick={() => { swapTargetIdRef.current = selectedAsset.id; setSwapCameraActive(true); }}>
              {tl('swapCamera')}
            </Button>
          ) : (
            <Button size='sm' icon={<Mic />} disabled={busy} className='flex-1'
              onClick={() => { swapTargetIdRef.current = selectedAsset.id; setSwapRecordingActive(true); void startSwapRecording(); }}>
              {tl('swapCamera')}
            </Button>
          )}
        </div>
      )}
      </div>

      {/* Text-option pager — pick a caption/alt-text (or transcript) combo to carry into the new placement */}
      <div className='flex flex-col gap-1.5 rounded-md border border-ws-border bg-ws-surface p-2.5'>
        {usageLoading ? (
          <p className='text-xs text-ws-muted'>{tl('loading')}</p>
        ) : (
          <>
            <div className='flex items-center justify-between gap-2'>
              <button type='button' disabled={pagerIndex === 0} aria-label={tl('previousUsage')} onClick={() => setPagerIndex((i) => i - 1)} className='rounded p-1 text-ws-muted hover:bg-ws-hover disabled:opacity-30'>
                <ChevronLeft className='h-4 w-4' />
              </button>
              <span className='min-w-0 flex-1 truncate text-center text-xs text-ws-text'>
                {currentOption ? (
                  <span className='font-medium'>{currentOption.sampleExperienceTitle} → {currentOption.sampleTaskTitle}</span>
                ) : (
                  <i className='text-ws-muted'>{tl('blankOption')}</i>
                )}
              </span>
              <button type='button' disabled={pagerIndex >= totalPagerSteps - 1} aria-label={tl('nextUsage')} onClick={() => setPagerIndex((i) => i + 1)} className='rounded p-1 text-ws-muted hover:bg-ws-hover disabled:opacity-30'>
                <ChevronRight className='h-4 w-4' />
              </button>
            </div>
            <p className='text-center text-[10px] uppercase tracking-wide text-ws-muted'>
              {tl('usageCount', { current: pagerIndex + 1, total: totalPagerSteps })}
              {currentOption && currentOption.count > 1 ? ` · ${tl('moreElsewhere', { count: currentOption.count - 1 })}` : ''}
            </p>
            {/*
              * ONE LAYOUT FOR EVERY STEP, including blank — the same labelled rows, reading
              * "none" where there is nothing.
              *
              * Blank used to render a sentence instead ("Start with no alt text or caption, you'll
              * fill these in after placing"), which described what would happen next and read as
              * though a caption had already been chosen. Rendering nothing at all was the other
              * obvious answer and is worse: the panel loses two lines and the whole box resizes
              * under the pointer each time you step on or off blank, which on a control you page
              * through is exactly where a jump is most felt.
              *
              * A labelled row saying "none" also states the absence rather than leaving it to be
              * inferred from empty space.
              */}
            {selectedAsset.kind === 'audio' ? (
              // Clamped — an uncapped transcript can run long enough to push "Use this clip"
              // below the fold, forcing a scroll just to confirm the pick.
              <p className='line-clamp-3 text-xs text-ws-text'>
                <span className='font-medium'>{t('blocks.sound.transcript')}: </span>
                {currentOption?.transcript ? currentOption.transcript : <i className='text-ws-muted'>{tl('noneValue')}</i>}
              </p>
            ) : (
              /*
               * CLAMPED, all of them, for the reason the transcript above already gives: these
               * rows sit directly above "Use this image", and an uncapped one pushes the button
               * off-screen so the pick cannot be confirmed without scrolling. The transcript was
               * fixed when that bit on the audio side; the image side has three fields and was
               * never given the same treatment, so it bit harder.
               *
               * The motion row only appears for an asset that moves, so a still's panel is
               * exactly as tall as it was.
               */
              <>
                <p className='line-clamp-3 text-xs text-ws-text'>
                  <span className='font-medium'>{t('blocks.image.altText')}: </span>
                  {currentOption?.altText ? currentOption.altText : <i className='text-ws-muted'>{tl('noneValue')}</i>}
                </p>
                {selectedAsset.animated && (
                  <p className='line-clamp-3 text-xs text-ws-text'>
                    <span className='font-medium'>{t('blocks.image.motionDescription')}: </span>
                    {currentOption?.motionDescription ? currentOption.motionDescription : <i className='text-ws-muted'>{tl('noneValue')}</i>}
                  </p>
                )}
                <p className='line-clamp-3 text-xs text-ws-text'>
                  <span className='font-medium'>{t('blocks.image.caption')}: </span>
                  {/* STRIPPED, unlike the two rows above it. A caption is the one rich-text field
                      of the three, so printing it raw showed a teacher
                      `<p>Animated WebP, transcoded from a GIF.</p>` where the sentence should be.
                      Not rendered as HTML either: this is a one-line summary to choose by, and
                      `stripHtmlForLabel` is what every other label surface already uses. */}
                  {currentOption?.caption ? stripHtmlForLabel(currentOption.caption) : <i className='text-ws-muted'>{tl('noneValue')}</i>}
                </p>
              </>
            )}
          </>
        )}
      </div>

    </>
  );

  /**
   * The primary action, OUTSIDE the scrolling body so it cannot be scrolled away from.
   *
   * It used to sit at the end of the scrolling column, which meant a long alt text or caption —
   * exactly what this panel exists to let you read before inserting — pushed "Use This Image" off
   * the bottom. Clamping the text rows helped and did not fix it: the image, the name, the date,
   * the pager and the rows together still overflow a short window.
   *
   * NOT the `mt-auto` the previous comment here rejected, and the reason that comment gave does
   * not apply. An auto-margin recalculates from sibling sizes inside one flex column, which could
   * shift this row out from under an actively-hovered pointer and fire a spurious pointerleave
   * mid-arm. A separate non-scrolling footer has no such dependency — its position is fixed by the
   * container, whatever the body does.
   */
  const detailFooter = selectedAsset && (
      <div className='flex items-center gap-2'>
        <div aria-live='assertive' aria-atomic='true' className='sr-only'>{deleteArmed ? t('blocks.actions.deleteArmed') : ''}</div>
        <Button variant='primary' onClick={handleUseImage} className='flex-1'>
          {tl('useImage')}
        </Button>
        <IconButton
          variant='destructive'
          size='sm'
          isArmed={deleteArmed}
          disabled={deleteBlocked}
          title={effectiveUsage && effectiveUsage.blockCount > 0 ? tl('deleteBlockedInUse', { blockCount: effectiveUsage.blockCount }) : undefined}
          aria-label={
            effectiveUsage && effectiveUsage.blockCount > 0
              ? tl('deleteBlockedInUse', { blockCount: effectiveUsage.blockCount })
              : deleteArmed
                ? tl('deleteConfirm')
                : tl('deleteLabel')
          }
          onPointerDown={(e) => { if (e.button !== 0 || deleteBlocked) return; if (deleteArmed) void handleDelete(selectedAsset); else setDeleteArmed(true); }}
          onPointerLeave={(e) => { if (deleteArmed && e.pointerType !== 'touch') setDeleteArmed(false); }}
          onBlur={() => setDeleteArmed(false)}
          onClick={(e) => { if (deleteBlocked) return; if (e.detail === 0) { if (deleteArmed) void handleDelete(selectedAsset); else setDeleteArmed(true); } }}
        >
          <Trash2 className='h-3.5 w-3.5' />
        </IconButton>
      </div>
  );

  return (
    <div
      ref={overlayRef}
      role='presentation'
      className='fixed inset-0 z-50 flex items-center justify-center bg-ws-modal-overlay p-4'
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role='dialog'
        aria-modal='true'
        /* Labelled BY the visible heading rather than with a parallel `aria-label` — the two had
           already drifted, since the heading says "Recently Deleted" in trash view and the label
           did not. Matches `ModalShell`, which every other dialog in the app is built on. */
        aria-labelledby={titleId}
        className='flex w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-ws-border bg-ws-elevated shadow-2xl'
        style={{ height: 'min(90dvh, 760px)' }}
      >
        {/* Header */}
        <div className='flex shrink-0 items-center justify-between border-b border-ws-border px-4 py-3'>
          <h2 id={titleId} className='text-sm font-semibold text-ws-text'>
            {/* 'any' mode gets a genuinely neutral title, not the image/sound-specific one
                `libraryNs` would otherwise default to before anything's selected. */}
            {showTrash ? tl('trashTitle') : assetKind === 'any' ? t('common.mediaLibrary') : tl('modalTitle')}
          </h2>
          <button type='button' aria-label={t('common.close')} onClick={onClose} className='rounded-lg p-1.5 text-ws-muted hover:bg-ws-hover hover:text-ws-text'>
            <X className='h-4 w-4' />
          </button>
        </div>

        {showTrash ? (
          <div className='flex shrink-0 items-center justify-between border-b border-ws-border px-4 py-3'>
            <button ref={trashBackButtonRef} type='button' onClick={() => setShowTrash(false)} className='text-xs font-medium text-ws-muted hover:text-ws-text'>
              {tl('backToLibrary')}
            </button>
          </div>
        ) : !(isMobile && selectedAsset) ? (
          <div className='flex shrink-0 flex-col gap-3 border-b border-ws-border px-4 py-3'>
            {/* Filters (scope + Recently Deleted, grouped together) and actions (Upload Media +
                live capture) sit side by side on a wide modal, but stack — filters on top, actions
                below — once there's not enough width for both, which is what actually keeps
                Recently Deleted from getting crowded out on a narrow/portrait phone instead of a
                fixed mobile-only special case. */}
            <div className={`flex gap-3 ${isMobilePortrait ? 'flex-col' : 'items-center justify-between'}`}>
              <div className='flex items-center gap-3'>
                {/* A radiogroup, not tabs — these switch a data filter, not distinct content
                    panels, and mutual exclusivity is exactly radio semantics. Roving tabindex +
                    arrow keys match the ARIA Authoring Practices radiogroup pattern. */}
                <div role='radiogroup' aria-label={tl('scopeLabel')} className='inline-flex w-fit rounded-lg border border-ws-border p-0.5'>
                  {SCOPE_KEYS.map((s, i) => (
                    <button
                      key={s.value}
                      ref={(el) => { scopeRadioRefs.current[i] = el; }}
                      type='button'
                      role='radio'
                      aria-checked={scope === s.value}
                      tabIndex={scope === s.value ? 0 : -1}
                      onClick={() => setScope(s.value)}
                      onKeyDown={(e) => {
                        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
                        e.preventDefault();
                        const dir = e.key === 'ArrowRight' ? 1 : -1;
                        const nextIndex = (i + dir + SCOPE_KEYS.length) % SCOPE_KEYS.length;
                        setScope(SCOPE_KEYS[nextIndex]!.value);
                        scopeRadioRefs.current[nextIndex]?.focus();
                      }}
                      className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${scope === s.value ? 'bg-ws-btn-active text-ws-btn-active-fg' : 'text-ws-muted hover:text-ws-text'}`}
                    >
                      {s.value === 'all' ? gridT('scopeAll') : tl(s.key)}
                    </button>
                  ))}
                </div>
                <button ref={viewTrashButtonRef} type='button' onClick={openTrash} className='shrink-0 text-xs font-medium text-ws-muted hover:text-ws-text'>
                  {tl('viewTrash')}
                </button>
              </div>
              <div className='flex shrink-0 flex-wrap items-center gap-2'>
                {addNewButton}
                {takePhotoButton}
                {recordSoundButton}
              </div>
            </div>
            {/* The placeholder stays, against the no-ghost-text rule, because it names a SCOPE
                the glass cannot: "Search your images" against "Search your clips" is the only
                thing on screen saying which library this box narrows. */}
            <SearchField
              inputRef={searchInputRef}
              value={search}
              onChange={setSearch}
              placeholder={gridT('searchPlaceholder')}
              ariaLabel={gridT('searchPlaceholder')} />
          </div>
        ) : null}

        {showTrash ? (
          /* Trash — a simple list; restore/purge aren't a selection flow, so no grid/panel here */
          <div className='flex-1 overflow-y-auto px-4 py-3'>
            {trashLoading ? (
              <p className='py-8 text-center text-sm text-ws-muted'>{tl('loading')}</p>
            ) : !trashed || trashed.length === 0 ? (
              <p className='py-8 text-center text-sm text-ws-muted'>{gridT('trashEmpty')}</p>
            ) : (
              <div className='flex flex-col gap-2'>
                {trashed.map((asset) => {
                  const purgeArmed = purgeArmedId === asset.id;
                  return (
                    <div key={asset.id} className={`flex flex-col gap-2 rounded-lg border p-2.5 ${purgeArmed ? 'border-ws-warning-fg bg-ws-warning-bg' : 'border-ws-border'}`}>
                      <div className='flex items-center gap-3'>
                        {asset.kind === 'audio' ? (
                          <div
                            className='flex h-12 w-12 shrink-0 flex-col items-center justify-center gap-0.5 rounded p-1'
                            style={{ backgroundColor: assetAccentColor(asset.id) }}
                          >
                            <MiniWaveform peaks={asset.waveformPeaks} className='flex-1' />
                            <span className='text-[8px] text-ws-muted'>{formatDuration(asset.durationSeconds)}</span>
                          </div>
                        ) : (
                          <img src={asset.urls.thumb} alt='' loading='lazy' className='h-12 w-12 shrink-0 rounded object-cover' />
                        )}
                        <div className='min-w-0 flex-1'>
                          <p className='truncate text-sm font-medium text-ws-text'>{asset.filename}</p>
                          <p className='text-xs text-ws-muted'>{tl('uploadedOn', { date: formatUploadDate(asset.createdAt, i18n.language) })}</p>
                          <p className='text-xs text-ws-muted'>{tl('purgesInDays', { days: daysUntilPurge(asset.purgeAt) })}</p>
                        </div>
                      </div>
                      <div className='flex items-center gap-2'>
                        <div aria-live='assertive' aria-atomic='true' className='sr-only'>{purgeArmed ? tl('purgeArmedAnnouncement') : ''}</div>
                        <Button size='sm' icon={<RotateCcw />} disabled={busy}
                          onClick={() => void handleRestore(asset)}>
                          {tl('restore')}
                        </Button>
                        <IconButton
                          variant='destructive'
                          size='sm'
                          isArmed={purgeArmed}
                          aria-label={purgeArmed ? tl('purgeConfirm') : tl('purgeForever')}
                          title={tl('purgeForever')}
                          onPointerDown={(e) => { if (e.button !== 0) return; if (purgeArmed) void handlePurgeForever(asset); else setPurgeArmedId(asset.id); }}
                          onPointerLeave={(e) => { if (purgeArmed && e.pointerType !== 'touch') setPurgeArmedId(null); }}
                          onBlur={() => setPurgeArmedId(null)}
                          onClick={(e) => { if (e.detail === 0) { if (purgeArmed) void handlePurgeForever(asset); else setPurgeArmedId(asset.id); } }}
                        >
                          <Trash2 className='h-3.5 w-3.5' />
                        </IconButton>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : isMobile ? (
          selectedAsset ? (
            /* Mobile: full-screen replace-in-place — filmstrip up top (native horizontal scroll
               doubles as swipe-to-switch), details fill the rest. Closing (X) returns to the grid. */
            <div className='flex flex-1 flex-col overflow-hidden'>
              <div className='flex shrink-0 gap-2 overflow-x-auto border-b border-ws-border p-2.5'>
                {results?.map((asset) => (
                  <button
                    key={asset.id}
                    type='button'
                    onClick={() => selectAsset(asset)}
                    aria-label={asset.id === currentAssetId ? tl('currentlyUsedNamed', { filename: asset.filename }) : asset.filename}
                    aria-current={asset.id === selectedAssetId ? 'true' : undefined}
                    className={`relative h-14 w-14 shrink-0 overflow-hidden rounded-lg border-2 ${
                      asset.id === selectedAssetId ? 'border-ws-primary' : 'border-transparent'
                    }`}
                  >
                    {asset.kind === 'audio' ? (
                      <div className='flex h-full w-full items-center justify-center p-1' style={{ backgroundColor: assetAccentColor(asset.id) }}>
                        <MiniWaveform peaks={asset.waveformPeaks} className='h-full' />
                      </div>
                    ) : (
                      <img src={asset.urls.thumb} alt='' loading='lazy' className='h-full w-full object-cover' />
                    )}
                    {asset.id === currentAssetId && (
                      <span className='absolute right-0.5 top-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-ws-primary text-white'>
                        <Check className='h-2 w-2' />
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <div className={`flex min-h-0 flex-1 flex-col ${deleteArmed ? 'bg-ws-warning-bg' : ''}`}>
                <div className='flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4'>{detailBody}</div>
                <div className='shrink-0 border-t border-ws-border p-4'>{detailFooter}</div>
              </div>
            </div>
          ) : (
            <div className='flex-1 overflow-y-auto px-4 py-3'>{gridView}</div>
          )
        ) : (
          /* Desktop: grid + side panel */
          <div className='flex flex-1 overflow-hidden'>
            <div className='flex-1 overflow-y-auto px-4 py-3'>{gridView}</div>
            {selectedAsset && (
              <div className={`flex w-80 min-h-0 shrink-0 flex-col ${deleteArmed ? 'border-l border-ws-warning-fg bg-ws-warning-bg' : 'border-l border-ws-border'}`}>
                <div className='flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4'>{detailBody}</div>
                <div className='shrink-0 border-t border-ws-border p-4'>{detailFooter}</div>
              </div>
            )}
          </div>
        )}

        <input
          ref={swapInputRef}
          type='file'
          accept={
            selectedAsset
              ? (selectedAsset.kind === 'audio' ? 'audio/*' : IMAGE_ACCEPT)
              : (assetKind === 'audio' ? 'audio/*' : assetKind === 'image' ? IMAGE_ACCEPT : `${IMAGE_ACCEPT},audio/*`)
          }
          className='sr-only'
          /* OUT OF THE TAB ORDER. `sr-only` hides it from sight but NOT from the keyboard, so
             this sat in the tab order as a silent extra stop beside the visible control that
             opens it: a file input with no label, no `aria-label` and nothing to announce. It
             is driven by a ref from that control and is not the accessible affordance — the
             button is. (`hidden`, which the avatar and CSV pickers use, has never had this
             problem: `display:none` is not focusable.) */
          tabIndex={-1}
          onChange={handleSwapFileChange}
        />
      </div>
      {toastMsg && <Toast message={toastMsg} type='error' onDismiss={() => setToastMsg('')} />}
    </div>
  );
}
